/**
 * MoveHistory implementation maintains an array of move entries as a reactive signal.
 * Each entry is assigned a unique ID and timestamp when added. The implementation
 * supports updating the last entry, which is useful when move processing is async
 * and the response arrives after the initial entry is created. Clearing the history
 * resets the signal to an empty array.
 */
import { signal } from '@preact/signals-react'
import type { MoveHistoryEntry, MoveHistory } from './moveHistory.types'
import { getRandomId } from '@merels/game-logic'

/**
 * Associates move-history entries with snapshots reconstructed from the room action log.
 */
export function withReconstructedGameStates(
  entries: MoveHistoryEntry[],
  states: unknown[],
  divergences?: boolean[],
): MoveHistoryEntry[] {
  return entries.map((entry) => {
    if (entry.sequenceIndex === undefined || states[entry.sequenceIndex] === undefined) {
      return entry
    }
    return {
      ...entry,
      gameState: states[entry.sequenceIndex],
      divergent: divergences?.[entry.sequenceIndex],
    }
  })
}

export function makeMoveHistory(): MoveHistory {
  const entries = signal<MoveHistoryEntry[]>([])

  function addEntry(entry: Omit<MoveHistoryEntry, 'id' | 'timestamp'>): void {
    const newEntry: MoveHistoryEntry = {
      ...entry,
      id: getRandomId(5),
      timestamp: new Date().toISOString()
    }
    entries.value = [...entries.value, newEntry]
  }

  function updateLastEntry(updates: Partial<MoveHistoryEntry>): void {
    const currentEntries = entries.value
    if (currentEntries.length === 0) return

    const lastEntry = currentEntries[currentEntries.length - 1]
    const updatedEntry = { ...lastEntry, ...updates }
    const updatedEntries = [...currentEntries.slice(0, -1), updatedEntry]
    entries.value = updatedEntries
  }

  function clear(): void {
    entries.value = []
  }

  return {
    entries,
    addEntry,
    updateLastEntry,
    clear
  }
}
